<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import ReportsAPI from 'dashboard/api/reports';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const route = useRoute();
const isLoading = ref(false);
const errorText = ref('');
const total = ref(0);
const items = ref([]);
const targetAssistantId = ref('');
const statusFilter = ref('pending'); // speed default: show pending
const appliedAssistantFilter = ref('');

const accountId = computed(() => Number(route.params.accountId));
const hasDateRange = computed(() => props.filters?.from && props.filters?.to);

const conversationLink = conversationId =>
  frontendURL(
    conversationUrl({
      accountId: accountId.value,
      id: conversationId,
    })
  );

const fetchData = async () => {
  if (!hasDateRange.value) return;
  isLoading.value = true;
  errorText.value = '';
  try {
    const assistantId = Number(appliedAssistantFilter.value) || null;
    const response = await ReportsAPI.getBotImproveBacklog({
      from: props.filters.from,
      to: props.filters.to,
      status: statusFilter.value,
      assistantId,
      limit: 50,
    });
    total.value = response.data.total || 0;
    items.value = response.data.items || [];
  } catch (e) {
    errorText.value = e?.message || 'Failed to load improve backlog';
  } finally {
    isLoading.value = false;
  }
};

const isApplied = row => {
  return Boolean(row?.applied);
};

const toCsvCell = v => {
  const s = (v ?? '').toString().replaceAll('"', '""');
  return `"${s}"`;
};

const downloadCsv = () => {
  try {
    const headers = [
      'id',
      'created_at',
      'created_by_user_id',
      'kind',
      'normalized',
      'example',
      'sample_conversation_ids',
    ];
    const rows = (items.value || []).map(row => [
      row.id,
      row.created_at,
      row.created_by_user_id ?? '',
      row.kind,
      row.normalized,
      row.example,
      (row.sample_conversation_ids || []).join(','),
    ]);

    const csv =
      [headers, ...rows].map(r => r.map(toCsvCell).join(',')).join('\n') + '\n';

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wlab_ai_improve_backlog_${accountId.value}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    useAlert(e?.message || 'Failed to download CSV');
  }
};

const copyJson = async () => {
  try {
    const payload = {
      account_id: accountId.value,
      total: total.value,
      items: items.value,
    };
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    useAlert('Copied JSON to clipboard');
  } catch (e) {
    useAlert(e?.message || 'Failed to copy JSON');
  }
};

const copyDraft = async row => {
  try {
    const response = await ReportsAPI.getBotImproveDraft({ id: row.id });
    const md = response.data.markdown || '';
    if (!md) {
      useAlert('Draft is empty');
      return;
    }
    await navigator.clipboard.writeText(md);
    useAlert('Copied draft to clipboard');
  } catch (e) {
    useAlert(e?.message || 'Failed to copy draft');
  }
};

const copyTemplateCsv = async row => {
  try {
    const response = await ReportsAPI.getBotImproveTemplateCsv({ id: row.id });
    const csv = response.data.csv || '';
    if (!csv) {
      useAlert('CSV is empty');
      return;
    }
    await navigator.clipboard.writeText(csv);
    useAlert('Copied template CSV to clipboard');
  } catch (e) {
    useAlert(e?.message || 'Failed to copy template CSV');
  }
};

const downloadTemplateCsv = async row => {
  try {
    const response = await ReportsAPI.getBotImproveTemplateCsv({ id: row.id });
    const csv = response.data.csv || '';
    if (!csv) {
      useAlert('CSV is empty');
      return;
    }
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wlab_ai_template_event_${row.id}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    useAlert(e?.message || 'Failed to download template CSV');
  }
};

const previewApplyTemplate = async row => {
  try {
    const assistantId = Number(targetAssistantId.value);
    if (!assistantId) {
      useAlert('Target assistant_id is required');
      return;
    }
    const response = await ReportsAPI.applyBotImproveTemplate({
      id: row.id,
      assistantId,
      dryRun: true,
    });
    const r = response.data.row || {};
    useAlert(
      `Preview: lang=${r.language || '-'} category=${r.category || '-'} priority=${r.priority ?? '-'}`
    );
  } catch (e) {
    useAlert(e?.message || 'Failed to preview apply');
  }
};

const applyTemplate = async row => {
  try {
    const assistantId = Number(targetAssistantId.value);
    if (!assistantId) {
      useAlert('Target assistant_id is required');
      return;
    }
    const ok = window.confirm(
      `Apply template for improve item #${row.id} to assistant_id=${assistantId}?`
    );
    if (!ok) return;
    await ReportsAPI.applyBotImproveTemplate({
      id: row.id,
      assistantId,
      dryRun: false,
    });
    useAlert('Applied template');
    fetchData();
  } catch (e) {
    useAlert(e?.message || 'Failed to apply template');
  }
};

const applyAllPending = async () => {
  try {
    const assistantId = Number(targetAssistantId.value);
    if (!assistantId) {
      useAlert('Target assistant_id is required');
      return;
    }
    const pending = (items.value || []).filter(r => !isApplied(r));
    if (!pending.length) {
      useAlert('No pending items');
      return;
    }
    const ok = window.confirm(
      `Apply ${pending.length} pending items to assistant_id=${assistantId}?`
    );
    if (!ok) return;

    let success = 0;
    for (const row of pending) {
      // eslint-disable-next-line no-await-in-loop
      await ReportsAPI.applyBotImproveTemplate({
        id: row.id,
        assistantId,
        dryRun: false,
      });
      success += 1;
    }
    useAlert(`Applied ${success}/${pending.length} templates`);
    fetchData();
  } catch (e) {
    useAlert(e?.message || 'Failed to apply all');
  }
};

watch(() => props.filters, fetchData, { deep: true });
onMounted(fetchData);
</script>

<template>
  <div
    class="mx-0 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
  >
    <div class="flex items-start justify-between gap-4">
      <div class="flex flex-col gap-1">
        <div class="text-base font-semibold text-slate-800 dark:text-white">
          {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.HEADER') }}
        </div>
        <div class="text-sm text-slate-600 dark:text-white/60">
          {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.DESCRIPTION') }}
        </div>
      </div>
      <div v-if="hasDateRange" class="backlog-controls">
        <span class="backlog-total">
          {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.TOTAL', { count: total }) }}
        </span>

        <div class="backlog-filter-row">
          <label class="backlog-field-label">
            {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.ACTIONS.STATUS') }}
          </label>
          <select
            v-model="statusFilter"
            class="status-select"
            @change="fetchData"
          >
            <option value="pending">
              {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.STATUS.PENDING') }}
            </option>
            <option value="applied">
              {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.STATUS.APPLIED') }}
            </option>
            <option value="all">
              {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.ACTIONS.STATUS_ALL') }}
            </option>
          </select>
        </div>

        <div class="backlog-filter-row">
          <label class="backlog-field-label">
            {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.ACTIONS.APPLIED_ASSISTANT_ID') }}
          </label>
          <input
            v-model="appliedAssistantFilter"
            type="number"
            min="1"
            class="assistant-id-input"
            :placeholder="
              $t(
                'BOT_REPORTS.IMPROVE_BACKLOG.ACTIONS.APPLIED_ASSISTANT_ID_PLACEHOLDER'
              )
            "
            @change="fetchData"
          />
        </div>

        <div class="backlog-filter-row">
          <label class="backlog-field-label">
            {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.ACTIONS.TARGET_ASSISTANT_ID') }}
          </label>
          <input
            v-model="targetAssistantId"
            type="number"
            min="1"
            class="assistant-id-input"
            :placeholder="
              $t(
                'BOT_REPORTS.IMPROVE_BACKLOG.ACTIONS.TARGET_ASSISTANT_ID_PLACEHOLDER'
              )
            "
          />
        </div>

        <div class="backlog-actions">
          <button
            class="secondary-action-btn"
            :disabled="!items.length"
            @click="downloadCsv"
          >
            {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.ACTIONS.DOWNLOAD_CSV') }}
          </button>
          <button
            class="secondary-action-btn"
            :disabled="!items.length"
            @click="copyJson"
          >
            {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.ACTIONS.COPY_JSON') }}
          </button>
          <button
            class="secondary-action-btn"
            :disabled="!items.length"
            @click="applyAllPending"
          >
            {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.ACTIONS.APPLY_ALL_PENDING') }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="!hasDateRange"
      class="mt-4 text-sm text-slate-600 dark:text-white/60"
    >
      {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.EMPTY_STATE') }}
    </div>

    <div v-else class="mt-4">
      <div v-if="isLoading" class="text-sm text-slate-600 dark:text-white/60">
        {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.LOADING') }}
      </div>
      <div v-else-if="errorText" class="text-sm text-red-600">
        {{ errorText }}
      </div>
      <div
        v-else-if="!items.length"
        class="text-sm text-slate-600 dark:text-white/60"
      >
        {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.NO_DATA') }}
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm mt-2">
          <thead>
            <tr class="text-left text-slate-600 dark:text-white/60">
              <th class="py-2 pr-4 font-medium">
                {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.TABLE.ITEM') }}
              </th>
              <th class="py-2 pr-4 font-medium w-40">
                {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.TABLE.CREATED_AT') }}
              </th>
              <th class="py-2 pr-4 font-medium w-28">
                {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.TABLE.CREATED_BY') }}
              </th>
              <th class="py-2 pr-4 font-medium w-36">
                {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.TABLE.STATUS') }}
              </th>
              <th class="py-2 pr-4 font-medium w-56">
                {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.TABLE.SAMPLES') }}
              </th>
              <th class="py-2 pr-4 font-medium w-40" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in items"
              :key="row.id"
              class="border-t border-white/5"
            >
              <td class="py-3 pr-4">
                <div class="text-slate-800 dark:text-white whitespace-pre-wrap">
                  {{ row.example || row.normalized }}
                </div>
                <div class="text-xs text-slate-600 dark:text-white/60 mt-1">
                  {{ row.kind }} · {{ row.normalized }}
                </div>
              </td>
              <td class="py-3 pr-4 text-slate-800 dark:text-white">
                {{ row.created_at }}
              </td>
              <td class="py-3 pr-4 text-slate-800 dark:text-white">
                {{ row.created_by_user_id || '-' }}
              </td>
              <td class="py-3 pr-4">
                <div
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold"
                  :class="
                    row.applied
                      ? 'status-badge status-applied'
                      : 'status-badge status-pending'
                  "
                >
                  {{
                    row.applied
                      ? $t('BOT_REPORTS.IMPROVE_BACKLOG.STATUS.APPLIED')
                      : $t('BOT_REPORTS.IMPROVE_BACKLOG.STATUS.PENDING')
                  }}
                </div>
                <div
                  v-if="row.applied"
                  class="text-xs text-slate-600 dark:text-white/60 mt-1"
                >
                  at {{ row.applied_at }} · assistant
                  {{ row.applied_assistant_id || '-' }}
                </div>
              </td>
              <td class="py-3 pr-4">
                <div class="flex flex-wrap gap-2">
                  <a
                    v-for="(
                      cid, index
                    ) in row.sample_conversation_display_ids ||
                    row.sample_conversation_ids ||
                    []"
                    :key="`${cid}-${index}`"
                    class="text-n-brand underline"
                    :href="conversationLink(cid)"
                    target="_blank"
                    rel="noreferrer"
                  >
                    #{{ cid }}
                  </a>
                </div>
              </td>
              <td class="py-3 pr-4">
                <div class="flex flex-col gap-2 items-start">
                  <button class="secondary-action-btn" @click="copyDraft(row)">
                    {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.TABLE.COPY_DRAFT') }}
                  </button>
                  <button
                    class="secondary-action-btn"
                    @click="copyTemplateCsv(row)"
                  >
                    {{
                      $t('BOT_REPORTS.IMPROVE_BACKLOG.TABLE.COPY_TEMPLATE_CSV')
                    }}
                  </button>
                  <button
                    class="secondary-action-btn"
                    @click="downloadTemplateCsv(row)"
                  >
                    {{
                      $t(
                        'BOT_REPORTS.IMPROVE_BACKLOG.TABLE.DOWNLOAD_TEMPLATE_CSV'
                      )
                    }}
                  </button>
                  <button
                    class="secondary-action-btn"
                    :disabled="isApplied(row)"
                    @click="previewApplyTemplate(row)"
                  >
                    {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.TABLE.PREVIEW_APPLY') }}
                  </button>
                  <button
                    class="secondary-action-btn"
                    :disabled="isApplied(row)"
                    @click="applyTemplate(row)"
                  >
                    {{ $t('BOT_REPORTS.IMPROVE_BACKLOG.TABLE.APPLY_TEMPLATE') }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.secondary-action-btn {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  color: #374151;
  white-space: nowrap;
}

.secondary-action-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.secondary-action-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.assistant-id-input {
  width: 110px;
  padding: 4px 8px;
  font-size: 12px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #111827;
}

.assistant-id-input:focus {
  outline: none;
  border-color: #6b7280;
}

.status-select {
  padding: 4px 8px;
  font-size: 12px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #111827;
  cursor: pointer;
}

.backlog-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
}

.backlog-total {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

.backlog-filter-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.backlog-field-label {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

.backlog-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.status-badge {
  border: 1px solid transparent;
}

.status-applied {
  background: #dcfce7;
  color: #15803d;
  border-color: #bbf7d0;
}

.status-pending {
  background: #fef9c3;
  color: #854d0e;
  border-color: #fef08a;
}
</style>
